(function () {
  "use strict";
  if (customElements.get("faf-bedroom-builder")) return;
  var Core = window.FafBedroomCore;
  if (!Core) return;
  var configurationRequests = new Map();

  function node(tag, className, text) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  }
  function replace(element, children) {
    while (element.firstChild) element.removeChild(element.firstChild);
    children.forEach(function (child) { element.appendChild(child); });
  }
  function normalizeSection(value) { return String(value || "").replace(/^floated-/, ""); }
  function selectionKey(selection) {
    return JSON.stringify(Object.keys(selection || {}).filter(function (key) { return selection[key] && selection[key].quantity > 0; }).sort().map(function (key) { return [key, selection[key]]; }));
  }
  function safeProductURL(handle, variantId) {
    if (!/^[a-z0-9][a-z0-9-]*$/.test(handle || "")) return null;
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || "/") + "products/" + handle + (variantId ? "?variant=" + encodeURIComponent(variantId) : "");
  }
  function imageURL(product, variant) {
    var image = variant && variant.featured_image || product && product.featured_image;
    var source = typeof image === "string" ? image : image && (image.src || image.url);
    if (!source) return null;
    try {
      var url = new URL(source, location.origin);
      if (url.protocol !== "https:" && !(url.protocol === "http:" && url.origin === location.origin)) return null;
      url.searchParams.set("width", "240");
      return url.href;
    } catch (_) { return null; }
  }
  async function getJSON(url, options, timeout) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, timeout || 18000);
    try {
      var response = await fetch(url, Object.assign({ credentials: "same-origin", cache: "no-store" }, options || {}, { signal: controller.signal }));
      var data;
      try { data = await response.json(); } catch (_) { data = null; }
      if (!response.ok || !data || data.status >= 400 || data.errors) {
        var error = new Error(data && (data.description || data.message) || "Unable to update the cart.");
        error.httpStatus = response.status;
        error.responseData = data;
        throw error;
      }
      return data;
    } finally { clearTimeout(timer); }
  }

  class BedroomBuilder extends HTMLElement {
    async connectedCallback() {
      if (this.closest("quick-add-modal, floated-form") || /^quickadd-|^floated-/.test(this.dataset.section || "")) {
        this.hidden = true;
        this.removeAttribute("id");
        return;
      }
      if (!("inert" in HTMLElement.prototype)) {
        this.hidden = true;
        this.removeAttribute("id");
        return;
      }
      if (this.initialized || this.initializing) return;
      this.initializing = true;
      var lifecycle = this.lifecycleVersion = (this.lifecycleVersion || 0) + 1;
      this.abort = new AbortController();
      this.purchaseSaved = new Map();
      this.busySaved = new Map();
      this.groupRows = new Map();
      this.presetButtons = new Map();
      this.nativeLabels = new Map();
      this.dataRevision = 0;
      this.contextVersion = 0;
      this.contextLoading = false;
      this.contextKey = null;
      this.lastBedVariantId = null;
      this.productTasks = this.productTasks || new Map();
      this.productQueue = this.productQueue || [];
      this.activeProductRequests = this.activeProductRequests || 0;
      this.productFailures = new Set();
      this.busy = false;
      this.blocked = false;
      this.unsupported = false;
      this.actionInView = true;
      this.successShown = false;
      this.section = this.closest('section[id^="MainProduct-"]');
      this.ui = this.querySelector("[data-bedroom-ui]");
      this.status = this.querySelector("[data-bedroom-status]");
      this.sticky = this.querySelector("[data-bedroom-sticky]");
      try {
        this.data = JSON.parse(this.querySelector("[data-bedroom-data]").textContent);
        this.products = this.data.products || {};
        this.strings = Object.assign({}, Core.strings, this.data.strings || {});
        this.family = this.data.family || await this.loadConfiguration();
        if (!this.isConnected || this.lifecycleVersion !== lifecycle) return;
        if (!this.section || !this.ui || !this.family || !this.products || !this.getForm()) throw new Error("Missing bedroom configuration");
        var inlineBed = this.products[this.family.bedHandle];
        if (this.dataset.bedHandle && this.family.bedHandle !== this.dataset.bedHandle || inlineBed && String(inlineBed.id) !== String(this.family.bedProductId)) throw new Error("Mismatched bedroom configuration");
        this.selection = Core.cloneSelection(this.family.defaultSelection);
        this.currency = this.dataset.currency || "USD";
        this.money = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || "en-US", { style: "currency", currency: this.currency });
        this.storageKey = "faf-bedroom-review:bed:" + this.family.bedProductId;
        this.legacyStorageKey = "faf-bedroom-review:" + this.family.id;
        this.restoreAttempt();
        this.render();
        this.bind();
        this.initialized = true;
        this.classList.add("faf-bedroom");
        this.id = "build-your-bedroom";
        this.hidden = false;
        this.sync(true);
        this.updateAnchorOffset();
        if (!this.unsupported && location.hash === "#build-your-bedroom") this.anchorFrame = requestAnimationFrame(() => {
          this.anchorFrame = null;
          if (this.isConnected && !this.unsupported) {
            this.updateAnchorOffset();
            this.scrollIntoView({ block: "start" });
          }
        });
        if (this.blocked) this.message(this.t("savedAttempt"), "error");
      } catch (error) {
        if (this.lifecycleVersion !== lifecycle) return;
        this.hidden = true;
        this.releasePurchaseState();
        console.error("Bedroom builder could not initialize", error);
      } finally { if (this.lifecycleVersion === lifecycle) this.initializing = false; }
    }

    disconnectedCallback() {
      this.lifecycleVersion = (this.lifecycleVersion || 0) + 1;
      this.contextVersion += 1;
      this.initializing = false;
      if (this.ui) this.clearPageClearance();
      if (this.abort) this.abort.abort();
      if (this.observer) this.observer.disconnect();
      if (this.intersection) this.intersection.disconnect();
      if (this.layoutObserver) this.layoutObserver.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame);
      if (this.anchorFrame) cancelAnimationFrame(this.anchorFrame);
      this.frame = this.layoutFrame = this.anchorFrame = null;
      this.style.removeProperty("--faf-bedroom-anchor-offset");
      this.releaseBusyState();
      this.releasePurchaseState();
      this.initialized = false;
    }

    async loadConfiguration() {
      var handle = this.dataset.bedHandle;
      if (!safeProductURL(handle) || !this.dataset.configUrl) throw new Error("Missing bedroom configuration URL");
      var url = new URL(this.dataset.configUrl, location.origin);
      if (url.origin !== location.origin && !(url.protocol === "https:" && url.hostname === "cdn.shopify.com")) throw new Error("Unexpected bedroom configuration host");
      if (!configurationRequests.has(url.href)) {
        var request = getJSON(url.href, { cache: "force-cache" }).catch(function (error) { configurationRequests.delete(url.href); throw error; });
        configurationRequests.set(url.href, request);
      }
      var data = await configurationRequests.get(url.href);
      var family = data && data.schemaVersion === 2 && data.families && data.families[handle];
      if (!family || family.bedHandle !== handle || !Array.isArray(family.groups) || !Array.isArray(family.bedVariants)) throw new Error("Invalid bedroom configuration");
      return family;
    }

    t(key, values) {
      var text = this.strings[key] || Core.strings[key] || key;
      Object.keys(values || {}).forEach(function (name) { text = text.split("{" + name + "}").join(String(values[name])); });
      return text;
    }

    format(cents) { return this.money.format(cents / 100); }

    getForm() {
      var form = document.getElementById(this.dataset.formId);
      return form && this.section && this.section.contains(form) && !form.closest("quick-add-modal, floated-form") ? form : null;
    }

    currentInputs() {
      var form = this.getForm();
      var quantity = form && form.querySelector('[name="quantity"]');
      var id = form && form.querySelector('[name="id"]');
      return { form: form, quantity: quantity, id: id, button: form && form.querySelector('[name="add"]'), bedrooms: quantity ? Number(quantity.value) : 1, bedVariantId: id && id.value };
    }

    plan() {
      return Core.compute(this.requestInput());
    }

    hasExtras() {
      if (this.unsupported) return false;
      return Object.keys(this.selection || {}).some((key) => this.selection[key] && this.selection[key].quantity > 0);
    }

    blockingReason() {
      var native = this.currentInputs();
      if (this.blocked) return this.t("blocked");
      if (this.busy) return this.t("adding");
      if (this.contextLoading) return this.t("optionsLoading");
      if (!native.form || !native.button || !native.id) return this.t("bedUnavailable");
      var pickerBusy = Array.from(this.section.querySelectorAll("variant-selects, variant-radios")).some(function (picker) { return !picker.closest("quick-add-modal") && picker.hasAttribute("aria-busy"); });
      if (pickerBusy) return this.t("bedLoading");
      if (native.form.closest("product-form") && native.form.closest("product-form").inert) return this.t("upsellPending");
      if (native.button.disabled || native.button.getAttribute("aria-disabled") === "true") return this.t("bedUnavailable");
      if (native.quantity && !native.quantity.checkValidity()) return this.t("invalidQuantity", { item: this.t("rooms") });
      return "";
    }

    render() {
      var header = node("div", "faf-bedroom__header");
      header.append(node("p", "faf-bedroom__eyebrow", this.t("eyebrow")), node("h2", "faf-bedroom__heading", this.t("heading", { family: this.family.label })), node("p", "faf-bedroom__intro", this.t("intro")));
      var presetGroup = node("div", "faf-bedroom__presets");
      presetGroup.setAttribute("role", "group");
      presetGroup.setAttribute("aria-label", this.t("presets"));
      this.family.presets.forEach((preset) => {
        var button = node("button", "faf-bedroom__preset", preset.label);
        button.type = "button";
        button.dataset.bedroomPreset = preset.key;
        button.setAttribute("aria-pressed", "false");
        if (preset.description) button.title = preset.description;
        presetGroup.append(button);
        this.presetButtons.set(preset.key, button);
      });
      var groups = node("div", "faf-bedroom__groups");
      this.family.groups.forEach((group) => groups.append(this.renderGroup(group)));
      var summary = node("div", "faf-bedroom__summary");
      this.summaryTitle = node("h3", "faf-bedroom__summary-title", this.t("summary"));
      this.roomNote = node("p", "faf-bedroom__note");
      this.summaryList = node("ul", "faf-bedroom__summary-list");
      var totalRow = node("div", "faf-bedroom__total-row");
      this.total = node("strong", "faf-bedroom__total");
      totalRow.append(node("span", "faf-bedroom__total-label", this.t("subtotal")), this.total);
      this.saving = node("p", "faf-bedroom__saving");
      this.packageNote = node("p", "faf-bedroom__note");
      this.packageIncludes = node("p", "faf-bedroom__note");
      this.action = node("button", "faf-bedroom__action button button--primary", this.t("bedOnly"));
      this.action.type = "button";
      this.action.dataset.bedroomAdd = "";
      this.action.setAttribute("aria-haspopup", "dialog");
      this.error = node("div", "faf-bedroom__error");
      this.error.hidden = true;
      this.errorText = node("p", "faf-bedroom__error-message");
      this.errorText.setAttribute("role", "alert");
      this.retry = node("button", "faf-bedroom__retry", this.t("refresh"));
      this.retry.type = "button";
      this.retry.dataset.bedroomRefresh = "";
      this.review = node("a", "faf-bedroom__link", this.t("reviewCart"));
      this.review.href = window.routes && window.routes.cart_url || "/cart";
      this.review.target = "_blank";
      this.review.rel = "noopener";
      this.review.dataset.bedroomReview = "";
      this.reviewReset = node("button", "faf-bedroom__retry", this.t("reviewReset"));
      this.reviewReset.type = "button";
      this.reviewReset.dataset.bedroomReset = "";
      this.error.append(this.errorText, this.retry, this.review, this.reviewReset);
      summary.append(this.summaryTitle, this.roomNote, this.summaryList, totalRow, this.saving, this.packageNote, this.packageIncludes, node("p", "faf-bedroom__note", this.t("taxNote")), this.action, this.error);
      replace(this.ui, [header, presetGroup, groups, summary]);
      if (this.sticky) {
        this.sticky.classList.add("faf-bedroom__sticky");
        var inner = node("div", "faf-bedroom__sticky-content");
        var prices = node("div", "faf-bedroom__sticky-prices");
        this.stickyTotal = node("strong", "faf-bedroom__sticky-price");
        prices.append(node("span", "faf-bedroom__note", this.t("subtotal")), this.stickyTotal);
        this.stickyAction = node("button", "faf-bedroom__sticky-action button button--primary", this.t("add"));
        this.stickyAction.type = "button";
        this.stickyAction.dataset.bedroomAdd = "";
        inner.append(prices, this.stickyAction);
        replace(this.sticky, [inner]);
      }
    }

    renderGroup(group) {
      var row = node("div", "faf-bedroom__group");
      row.dataset.bedroomGroup = group.key;
      var head = node("div", "faf-bedroom__group-head");
      var image = node("img", "faf-bedroom__thumb");
      image.width = 96;
      image.height = 96;
      image.loading = "lazy";
      image.decoding = "async";
      var content = node("div", "faf-bedroom__group-content");
      var inputId = "BedroomOption-" + this.dataset.section + "-" + group.key;
      var label = node("label", "faf-bedroom__label", group.label);
      label.htmlFor = inputId;
      var select = node("select", "faf-bedroom__select");
      select.id = inputId;
      select.dataset.bedroomChoice = group.key;
      var none = node("option", "", this.t("notAdded"));
      none.value = "";
      select.append(none);
      group.options.forEach((option) => {
        var choice = node("option", "", option.label);
        choice.value = option.key;
        select.append(choice);
      });
      var selectedCaption = node("p", "faf-bedroom__selected-caption");
      selectedCaption.id = inputId + "-Selected";
      select.setAttribute("aria-describedby", selectedCaption.id);
      content.append(label, node("span", "faf-bedroom__optional", this.t("optional")), select, selectedCaption);
      var qtyWrap = node("div", "faf-bedroom__quantity-wrap");
      var qty = node("select", "faf-bedroom__quantity");
      qty.id = inputId + "-Quantity";
      qty.dataset.bedroomQuantity = group.key;
      var qtyLabel = node("label", "faf-bedroom__quantity-label", this.t("quantity"));
      qtyLabel.htmlFor = qty.id;
      group.quantities.forEach(function (count) { var option = node("option", "", String(count)); option.value = String(count); qty.append(option); });
      qtyWrap.append(qtyLabel, qty);
      content.append(qtyWrap);
      head.append(image, content);
      var detail = node("details", "faf-bedroom__detail");
      var toggle = node("summary", "faf-bedroom__detail-toggle", this.t("details"));
      var body = node("div", "faf-bedroom__detail-body");
      detail.append(toggle, body);
      row.append(head, detail);
      this.groupRows.set(group.key, { row: row, select: select, selectedCaption: selectedCaption, qty: qty, qtyWrap: qtyWrap, image: image, detail: detail, body: body });
      return row;
    }

    bind() {
      var options = { signal: this.abort.signal };
      this.addEventListener("change", (event) => {
        var groupKey = event.target.dataset.bedroomChoice || event.target.dataset.bedroomQuantity;
        if (!groupKey || this.busy || this.blocked || this.contextLoading || this.unsupported) return;
        var row = this.groupRows.get(groupKey);
        var optionKey = row.select.value;
        if (event.target.dataset.bedroomChoice) {
          this.selection[groupKey] = optionKey ? { optionKey: optionKey, quantity: this.selection[groupKey] && this.selection[groupKey].quantity || 1 } : null;
        } else {
          var quantity = Number(row.qty.value);
          this.selection[groupKey] = quantity > 0 && optionKey ? { optionKey: optionKey, quantity: quantity } : null;
        }
        this.successShown = false;
        this.clearMessage();
        this.sync(true);
      }, options);
      this.addEventListener("click", (event) => {
        var target = event.target.closest("[data-bedroom-preset], [data-bedroom-add], [data-bedroom-refresh], [data-bedroom-review], [data-bedroom-reset]");
        if (!target || !this.contains(target)) return;
        if (target.hasAttribute("data-bedroom-review")) {
          this.reviewed = true;
          this.saveAttempt(Object.assign({}, this.attempt || {}, { reviewed: true }));
          this.refreshRecovery();
          return;
        }
        event.preventDefault();
        if (target.hasAttribute("data-bedroom-reset")) {
          if (!this.reviewed || this.busy) return;
          this.blocked = false;
          this.reviewed = false;
          this.clearAttempt();
          this.selection = Core.cloneSelection(this.family.defaultSelection);
          this.clearMessage();
          this.sync(true);
          return;
        }
        if (this.busy || this.blocked || this.contextLoading || this.unsupported) return;
        if (target.hasAttribute("data-bedroom-preset")) {
          var preset = this.family.presets.find(function (entry) { return entry.key === target.dataset.bedroomPreset; });
          if (!preset) return;
          var presetChoice = Core.presetSelection(preset, this.currentInputs().bedVariantId);
          if (!presetChoice) return;
          var previous = this.selection;
          this.selection = Core.cloneSelection(presetChoice);
          if (preset.key !== "bed-only") this.family.groups.forEach((group) => {
            if (group.options.some(function (option) { return !!option.variantsByBedSize; }) && previous[group.key]) this.selection[group.key] = previous[group.key];
          });
          this.successShown = false;
          this.clearMessage();
          this.sync(true);
        } else if (target.hasAttribute("data-bedroom-refresh")) this.refresh();
        else if (target.hasAttribute("data-bedroom-add")) this.requestAdd();
      }, options);
      this.section.addEventListener("submit", (event) => {
        var form = event.target;
        if (!(form instanceof HTMLFormElement) || form.closest("quick-add-modal") || this.unsupported) return;
        var owner = form.closest("product-form");
        var relevant = form === this.getForm() || owner && form.closest("floated-form") && normalizeSection(owner.dataset.source) === this.dataset.section;
        if (!relevant || (!this.hasExtras() && !this.blocked && !this.busy)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.blocked) { this.message(this.t("reviewFirst"), "error"); return; }
        if (!this.busy) this.add();
      }, { capture: true, signal: this.abort.signal });
      var nativeChange = (event) => {
        if (this.contains(event.target) || event.target.closest("quick-add-modal")) return;
        if (event.target.matches('[name="id"], [name="quantity"], [name="selling_plan"]') || event.target.closest("variant-radios, variant-selects")) this.scheduleSync();
      };
      this.section.addEventListener("change", nativeChange, options);
      this.section.addEventListener("input", nativeChange, options);
      document.addEventListener("variant:change", (event) => {
        if (normalizeSection(event.detail && event.detail.sectionId) === this.dataset.section) this.scheduleSync();
      }, options);
      this.observer = new MutationObserver((changes) => {
        if (changes.some((change) => !this.contains(change.target))) this.scheduleSync();
      });
      this.observer.observe(this.section, { subtree: true, childList: true, attributes: true, attributeFilter: ["disabled", "aria-disabled", "aria-busy", "value", "inert"] });
      if ("IntersectionObserver" in window) {
        this.intersection = new IntersectionObserver((entries) => { this.actionInView = entries[0].isIntersecting; this.updateSticky(); }, { threshold: 0.1 });
        this.intersection.observe(this.action);
      }
      this.header = document.querySelector(".shopify-section-header");
      if ("ResizeObserver" in window) {
        this.layoutObserver = new ResizeObserver(() => this.scheduleLayout());
        if (this.header) this.layoutObserver.observe(this.header);
        if (this.sticky) this.layoutObserver.observe(this.sticky);
      }
      window.addEventListener("resize", () => this.scheduleLayout(), options);
      window.addEventListener("orientationchange", () => this.scheduleLayout(), options);
      if (window.visualViewport) window.visualViewport.addEventListener("resize", () => this.scheduleLayout(), options);
    }

    scheduleLayout() {
      if (this.layoutFrame) return;
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null;
        if (!this.isConnected) return;
        this.updateAnchorOffset();
        this.updateSticky();
      });
    }

    updateAnchorOffset() {
      var header = this.header;
      if (!header || !header.isConnected) header = this.header = document.querySelector(".shopify-section-header");
      var height = header ? header.getBoundingClientRect().height : 0;
      var top = header ? Math.max(0, parseFloat(getComputedStyle(header).top) || 0) : 0;
      this.style.setProperty("--faf-bedroom-anchor-offset", Math.ceil(height + top + 16) + "px");
    }

    scheduleSync() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; if (this.isConnected) this.sync(); });
    }

    sync(force) {
      if (!this.ui || !this.family) return;
      var selectedBed = this.family.bedVariants.find((variant) => String(variant.variantId) === String(this.currentInputs().bedVariantId));
      if (!selectedBed) {
        this.unsupported = true;
        this.hidden = true;
        this.removeAttribute("id");
        this.contextVersion += 1;
        this.contextKey = null;
        this.contextLoading = false;
        this.dataset.activeExtras = "false";
        this.releasePurchaseState();
        this.updateSticky();
        return;
      }
      this.unsupported = false;
      this.hidden = false;
      this.id = "build-your-bedroom";
      var removed = [];
      if (this.lastBedVariantId && String(selectedBed.variantId) !== this.lastBedVariantId && !this.busy && !this.blocked) {
        this.family.groups.forEach((group) => {
          var choice = this.selection[group.key];
          var option = choice && group.options.find(function (entry) { return entry.key === choice.optionKey; });
          if (choice && !Core.resolveOption(option, selectedBed.size, selectedBed.variantId)) {
            this.selection[group.key] = null;
            removed.push(group.label);
          }
        });
      }
      this.lastBedVariantId = String(selectedBed.variantId);
      if (!this.busy && this.contextKey !== this.lastBedVariantId) this.loadContextProducts(selectedBed);
      var support = this.querySelector("[data-bedroom-support]");
      if (support) support.textContent = this.t(selectedBed.supportRequirement === "required" ? "supportRequired" : selectedBed.supportRequirement === "included" ? "supportIncluded" : "supportUnknown");
      this.applyPurchaseState();
      var native = this.currentInputs();
      var reason = this.blockingReason();
      var key = [native.bedVariantId, native.bedrooms, this.dataRevision, this.busy, this.blocked, this.successShown, reason, selectionKey(this.selection)].join("|");
      if (!force && this.syncKey === key) return;
      this.syncKey = key;
      this.result = this.plan();
      this.family.groups.forEach((group) => this.updateGroup(group));
      this.presetButtons.forEach((button, presetKey) => {
        var preset = this.family.presets.find(function (entry) { return entry.key === presetKey; });
        var presetChoice = Core.presetSelection(preset, selectedBed.variantId);
        var selected = !!presetChoice && selectionKey(this.selection) === selectionKey(presetChoice);
        button.hidden = !presetChoice;
        button.classList.toggle("is-selected", selected);
        button.setAttribute("aria-pressed", String(selected));
        var valid = this.presetAvailable(preset, selectedBed);
        button.disabled = this.busy || this.blocked || this.contextLoading || !valid;
        button.title = valid ? preset.description || "" : this.t("presetUnavailable");
      });
      var result = this.result;
      this.roomNote.textContent = native.bedrooms > 1 ? this.t("multiply", { count: native.bedrooms }) : this.t("single");
      replace(this.summaryList, result.contents.map((line) => {
        var row = node("li", "faf-bedroom__summary-line");
        row.append(node("span", "", line.quantity + " × " + line.label), node("span", "faf-bedroom__summary-price", this.format(line.price)));
        return row;
      }));
      this.total.textContent = result.ok ? this.format(result.total) : "—";
      this.saving.hidden = !(result.ok && result.savings > 0);
      this.saving.textContent = result.savings > 0 ? this.t("savings", { amount: this.format(result.savings) }) : "";
      this.packageNote.hidden = !result.packageMatch;
      this.packageIncludes.hidden = !result.packageMatch;
      this.packageNote.textContent = result.packageMatch ? this.t("packageNote") : "";
      this.packageIncludes.textContent = result.packageMatch ? this.t("packageContents", { items: result.packageMatch.includes.join("; ") }) : "";
      var label = this.busy ? this.t("adding") : this.hasExtras() ? this.t("add") : this.t("bedOnly");
      this.action.textContent = label;
      this.action.disabled = !!reason || !result.ok;
      this.action.title = reason;
      if (this.stickyAction) {
        this.stickyAction.textContent = label;
        this.stickyAction.disabled = this.action.disabled;
        this.stickyTotal.textContent = result.ok ? this.format(result.total) : "—";
      }
      if (!this.busy && !this.blocked && !this.contextLoading && !result.ok && result.errors.length) this.message(this.describeErrors(result.errors), "error");
      this.refreshRecovery();
      this.updateSticky();
      if (removed.length) this.message(this.t("selectionAdjusted", { items: removed.join(", ") }));
    }

    presetAvailable(preset, bed) {
      var presetChoice = Core.presetSelection(preset, bed.variantId);
      if (!presetChoice) return false;
      var compatible = this.family.groups.every(function (group) {
        var choice = presetChoice[group.key];
        if (!choice || !choice.quantity) return true;
        var option = group.options.find(function (entry) { return entry.key === choice.optionKey; });
        return !!Core.resolveOption(option, bed.size, bed.variantId);
      });
      return compatible && Core.compute({ family: this.family, products: this.products, bedVariantId: bed.variantId, bedrooms: this.currentInputs().bedrooms, selection: presetChoice }).ok;
    }

    updateGroup(group) {
      var controls = this.groupRows.get(group.key);
      var contextBed = this.family.bedVariants.find((variant) => String(variant.variantId) === String(this.currentInputs().bedVariantId));
      controls.row.hidden = !group.options.some(function (option) { return !!Core.resolveOption(option, contextBed && contextBed.size, contextBed && contextBed.variantId); });
      var selected = this.selection[group.key];
      controls.select.value = selected ? selected.optionKey : "";
      controls.qty.value = String(selected ? selected.quantity : 0);
      var hideQuantity = group.selectionMode !== "single-option-with-quantity" || !selected;
      var restoreQuantityFocus = hideQuantity && document.activeElement === controls.qty;
      controls.qtyWrap.hidden = hideQuantity;
      controls.select.disabled = this.busy || this.blocked || this.contextLoading;
      controls.qty.disabled = this.busy || this.blocked || this.contextLoading;
      Array.from(controls.select.options).forEach((entry) => {
        if (!entry.value) return;
        var baseSpec = group.options.find(function (option) { return option.key === entry.value; });
        var bedSpec = this.family.bedVariants.find((variant) => String(variant.variantId) === String(this.currentInputs().bedVariantId));
        var spec = Core.resolveOption(baseSpec, bedSpec && bedSpec.size, bedSpec && bedSpec.variantId);
        var found = Core.identify(this.products, spec);
        entry.textContent = baseSpec.label + (found ? " — " + this.format(found.price) : "") + (!spec && bedSpec ? " (" + (baseSpec.variantsByBedSize ? this.t("sizeUnavailable", { size: bedSpec.size }) : this.t("finishUnavailable")) + ")" : !found || !found.available ? " (" + this.t(this.contextLoading ? "optionsLoading" : "unavailable") + ")" : "");
        entry.disabled = !found || !found.available;
      });
      var baseOption = group.options.find(function (entry) { return selected && entry.key === selected.optionKey; }) || group.options[0];
      var bedSpec = this.family.bedVariants.find((variant) => String(variant.variantId) === String(this.currentInputs().bedVariantId));
      var option = Core.resolveOption(baseOption, bedSpec && bedSpec.size, bedSpec && bedSpec.variantId);
      var found = option && Core.identify(this.products, option);
      controls.selectedCaption.hidden = !selected || !found;
      replace(controls.selectedCaption, selected && found ? [node("span", "", baseOption.label), node("strong", "", this.format(found.price))] : []);
      var src = found && imageURL(found.product, found.variant);
      controls.image.hidden = !src;
      if (src && controls.image.src !== src) controls.image.src = src;
      controls.image.alt = option ? (option.variantsByBedSize ? option.label : this.family.label + " " + option.label) : "";
      var info = [];
      if (option) {
        info.push(node("p", "faf-bedroom__detail-name", option.variantsByBedSize ? option.label : this.family.label + " " + option.label));
        info.push(node("p", "", option.variantsByBedSize ? bedSpec.size : option.finishLabel || bedSpec.finishLabel || this.family.finishLabel || ""));
        if (option.dimensions && option.dimensions.text) info.push(node("p", "", this.t("dimensions") + ": " + option.dimensions.text + " " + (option.dimensions.unit || "")));
        else info.push(node("p", "", this.t("dimensionsUnknown")));
        var href = safeProductURL(option.productHandle, option.variantId);
        if (href) {
          var link = node("a", "faf-bedroom__link", this.t("productDetails"));
          link.href = href;
          link.target = "_blank";
          link.rel = "noopener";
          info.push(link);
        }
      }
      replace(controls.body, info);
      controls.row.classList.toggle("is-selected", !!selected);
      if (restoreQuantityFocus && !controls.row.hidden && !controls.select.disabled) controls.select.focus();
    }

    applyPurchaseState() {
      var active = !this.unsupported && (this.hasExtras() || this.blocked);
      this.dataset.activeExtras = String(active);
      this.section.classList.toggle("faf-bedroom-purchase-active", active);
      if (!active) { this.releasePurchaseState(); return; }
      this.section.querySelectorAll(".product-form__checkout, floated-form").forEach((element) => {
        if (element.closest("quick-add-modal")) return;
        if (!this.purchaseSaved.has(element)) this.purchaseSaved.set(element, { hidden: element.hidden, inert: element.inert });
        if (!element.hidden) element.hidden = true;
        if (!element.inert) element.inert = true;
      });
      var native = this.currentInputs();
      if (native.quantity) {
        var label = Array.from(native.form.querySelectorAll("label")).find(function (entry) { return entry.htmlFor === native.quantity.id; });
        if (label) {
          if (!this.nativeLabels.has(label)) this.nativeLabels.set(label, label.textContent);
          if (label.textContent !== this.t("rooms") + ":") label.textContent = this.t("rooms") + ":";
        }
      }
      var span = native.button && native.button.querySelector("span");
      if (span && !native.button.disabled && !this.busy && span.textContent !== this.t("add")) {
        if (!this.nativeLabels.has(span)) this.nativeLabels.set(span, span.textContent);
        span.textContent = this.t("add");
      }
    }

    releasePurchaseState() {
      if (this.section) this.section.classList.remove("faf-bedroom-purchase-active");
      if (this.purchaseSaved) {
        this.purchaseSaved.forEach(function (saved, element) { if (element.hidden !== saved.hidden) element.hidden = saved.hidden; if (element.inert !== saved.inert) element.inert = saved.inert; });
        this.purchaseSaved.clear();
      }
      if (this.nativeLabels) {
        this.nativeLabels.forEach((text, element) => { if (element.isConnected && (!element.closest("button") || element.textContent === this.t("add"))) element.textContent = text; });
        this.nativeLabels.clear();
      }
    }

    setBusy(value) {
      this.busy = value;
      this.setAttribute("aria-busy", String(value));
      if (value) {
        var form = this.getForm();
        var elements = Array.from(this.section.querySelectorAll("variant-radios, variant-selects")).filter(function (element) { return !element.closest("quick-add-modal"); });
        if (form) elements.push(form.closest("product-form"));
        elements.forEach((element) => { if (!this.busySaved.has(element)) this.busySaved.set(element, element.inert); if (!element.inert) element.inert = true; });
        var button = this.currentInputs().button;
        if (button) { this.loadingButton = button; this.wasLoading = button.classList.contains("loading"); button.classList.add("loading"); }
      } else this.releaseBusyState();
      this.sync(true);
    }

    releaseBusyState() {
      if (this.busySaved) { this.busySaved.forEach(function (inert, element) { if (element.inert !== inert) element.inert = inert; }); this.busySaved.clear(); }
      if (this.loadingButton && !this.wasLoading) this.loadingButton.classList.remove("loading");
      this.loadingButton = null;
    }

    updateSticky() {
      if (!this.sticky) return;
      this.sticky.hidden = !this.hasExtras() || this.actionInView || this.successShown;
      var rect = this.sticky.getBoundingClientRect();
      var visible = !this.sticky.hidden && rect.height > 0 && rect.width > 0;
      if (!visible) { this.clearPageClearance(); return; }
      var clearance = Math.ceil(Math.max(rect.height, window.innerHeight - rect.top) + 12);
      if (!this.pageClearance) {
        this.pageClearance = node("div", "faf-bedroom__page-clearance");
        this.pageClearance.setAttribute("aria-hidden", "true");
        document.body.appendChild(this.pageClearance);
      }
      this.pageClearance.style.height = clearance + "px";
      document.body.style.setProperty("--faf-bedroom-sticky-clearance", clearance + "px");
      document.body.classList.add("faf-bedroom-sticky-visible");
    }

    clearPageClearance() {
      if (this.pageClearance) this.pageClearance.remove();
      this.pageClearance = null;
      document.body.classList.remove("faf-bedroom-sticky-visible");
      document.body.style.removeProperty("--faf-bedroom-sticky-clearance");
    }

    describeErrors(errors) {
      return errors.map((error) => {
        if (error.code === "bed_identity" || error.code === "bed_unavailable") return this.t("bedUnavailable");
        if (error.code === "identity") return this.t("identityError", { item: error.label });
        if (error.code === "quantity") return this.t("invalidQuantity", { item: error.label });
        return this.t("optionError", { item: error.label });
      }).join(" ");
    }

    message(text, kind) {
      if (!this.isConnected || !this.error) return;
      if (kind === "error") {
        this.error.hidden = false;
        this.errorText.textContent = text;
        if (this.status) this.status.textContent = "";
      } else {
        this.error.hidden = true;
        this.errorText.textContent = "";
        if (this.status) this.status.textContent = text;
      }
      this.refreshRecovery();
    }

    clearMessage() {
      if (this.blocked) return;
      if (this.error) this.error.hidden = true;
      if (this.status) this.status.textContent = "";
    }

    refreshRecovery() {
      if (!this.error) return;
      this.retry.hidden = this.blocked;
      this.retry.disabled = this.busy;
      this.review.hidden = !this.blocked && !this.successShown;
      this.reviewReset.hidden = !this.blocked;
      this.reviewReset.disabled = !this.reviewed || this.busy;
    }

    saveAttempt(value) {
      value = Object.assign({}, value, { bedHandle: this.family.bedHandle, bedProductId: String(this.family.bedProductId) });
      this.attempt = value;
      try { sessionStorage.setItem(this.storageKey, JSON.stringify(value)); } catch (_) {}
    }

    clearAttempt() {
      this.attempt = null;
      try { sessionStorage.removeItem(this.storageKey); } catch (_) {}
      if (this.restoredLegacyAttempt) {
        try { sessionStorage.removeItem(this.legacyStorageKey); } catch (_) {}
        this.restoredLegacyAttempt = false;
      }
    }

    restoreAttempt() {
      try {
        var saved = JSON.parse(sessionStorage.getItem(this.storageKey));
        if (!saved && this.legacyStorageKey) {
          saved = JSON.parse(sessionStorage.getItem(this.legacyStorageKey));
          if (saved && saved.pending) this.restoredLegacyAttempt = true;
        }
        if (saved && saved.pending) {
          this.attempt = saved;
          this.blocked = true;
          this.reviewed = saved.reviewed === true;
          if (saved.selection && (!saved.bedHandle || saved.bedHandle === this.family.bedHandle)) this.selection = Core.cloneSelection(saved.selection);
        }
      } catch (_) {}
    }

    queueProduct(handle) {
      if (this.productTasks.has(handle)) return this.productTasks.get(handle);
      var promise = new Promise((resolve, reject) => { this.productQueue.push({ handle: handle, resolve: resolve, reject: reject }); });
      this.productTasks.set(handle, promise);
      this.pumpProductQueue();
      return promise;
    }

    pumpProductQueue() {
      while (this.activeProductRequests < 4 && this.productQueue.length) {
        var task = this.productQueue.shift();
        this.activeProductRequests += 1;
        this.runProductTask(task);
      }
    }

    async runProductTask(task) {
      try {
        var url = safeProductURL(task.handle);
        if (!url) throw new Error("Invalid product handle");
        var product = await getJSON(url + ".js");
        if (product.handle !== task.handle || !Array.isArray(product.variants)) throw new Error("Invalid product response");
        task.resolve({ handle: task.handle, product: product });
      } catch (error) { task.reject(error); }
      finally {
        this.productTasks.delete(task.handle);
        this.activeProductRequests -= 1;
        this.pumpProductQueue();
      }
    }

    async fetchProducts(handles) {
      return Promise.all(Array.from(new Set(handles)).map(async (handle) => {
        try { return Object.assign({ ok: true }, await this.queueProduct(handle)); }
        catch (error) { return { ok: false, handle: handle, error: error }; }
      }));
    }

    mergeProducts(results) {
      results.forEach((result) => {
        if (result.ok) {
          this.products[result.handle] = result.product;
          this.productFailures.delete(result.handle);
        } else {
          delete this.products[result.handle];
          this.productFailures.add(result.handle);
        }
      });
      this.dataRevision += 1;
    }

    async loadContextProducts(bed) {
      this.contextKey = String(bed.variantId);
      var context = ++this.contextVersion;
      var lifecycle = this.lifecycleVersion;
      var handles = Core.productHandles(this.family, bed.variantId, this.selection, false).filter((handle) => !this.products[handle]);
      this.contextLoading = handles.length > 0;
      if (!handles.length) return;
      this.message(this.t("optionsLoading"));
      var results = await this.fetchProducts(handles);
      if (!this.isConnected || this.lifecycleVersion !== lifecycle || this.contextVersion !== context) return;
      this.mergeProducts(results);
      this.contextLoading = false;
      this.sync(true);
      if (!this.blocked && !this.busy) {
        if (results.some(function (result) { return !result.ok; })) this.message(this.t("optionsUnavailable"), "error");
        else if (this.status && this.status.textContent === this.t("optionsLoading")) this.clearMessage();
      }
    }

    async refreshProducts(input) {
      var native = this.currentInputs();
      var request = input || { family: this.family, bedVariantId: native.bedVariantId, selection: this.selection };
      var lifecycle = this.lifecycleVersion;
      var context = this.contextVersion;
      var results = await this.fetchProducts(Core.productHandles(this.family, request.bedVariantId, request.selection, !!input, request.sellingPlan));
      if (this.isConnected === false || this.lifecycleVersion !== lifecycle || this.contextVersion !== context || String(this.currentInputs().bedVariantId) !== String(request.bedVariantId)) throw new Error("Bed selection changed during availability check");
      this.mergeProducts(results);
      if (input && results.some(function (result) { return !result.ok; })) throw new Error("Could not verify every selected item and eligible package");
      return results;
    }

    async refresh() {
      if (this.busy || this.blocked) return;
      this.setBusy(true);
      this.message(this.t("checking"));
      try {
        var results = await this.refreshProducts();
        if (results.some(function (result) { return !result.ok; })) this.message(this.t("optionsUnavailable"), "error");
        else this.clearMessage();
      }
      catch (_) { this.message(this.t("refreshFailed"), "error"); }
      finally { this.setBusy(false); }
    }

    requestAdd() {
      if (this.blocked || this.busy || this.unsupported) return;
      var reason = this.blockingReason();
      if (reason) { this.message(reason, "error"); return; }
      var native = this.currentInputs();
      if (!this.hasExtras()) {
        if (typeof native.form.requestSubmit === "function") native.form.requestSubmit(native.button);
        else native.button.click();
      } else this.add();
    }

    requestInput() {
      var native = this.currentInputs();
      var data = new FormData(native.form);
      var properties = {};
      data.forEach(function (value, key) {
        var match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === "string") properties[match[1]] = value;
      });
      return { family: this.family, products: this.products, bedVariantId: data.get("id"), bedrooms: data.has("quantity") ? Number(data.get("quantity")) : 1, selection: this.selection, baseProperties: properties, sellingPlan: data.get("selling_plan") || null };
    }

    async add() {
      if (this.busy || this.blocked || this.unsupported) return;
      var reason = this.blockingReason();
      if (reason) { this.message(reason, "error"); return; }
      var initial = Core.compute(this.requestInput());
      if (!initial.ok) { this.message(this.describeErrors(initial.errors), "error"); return; }
      if (!initial.hasExtras) { this.requestAdd(); return; }
      var before = null, plan = null, postStarted = false, response = null;
      var startingLifecycle = this.lifecycleVersion;
      var startingVariant = this.requestInput().bedVariantId;
      var base = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || "/";
      this.setBusy(true);
      this.message(this.t("checking"));
      try {
        await this.refreshProducts(this.requestInput());
        if (this.isConnected === false || this.lifecycleVersion !== startingLifecycle || String(this.requestInput().bedVariantId) !== String(startingVariant)) return;
        plan = Core.compute(this.requestInput());
        if (!plan.ok) { this.message(this.describeErrors(plan.errors), "error"); return; }
        if (initial.total !== plan.total || JSON.stringify(initial.items) !== JSON.stringify(plan.items)) { this.message(this.t("updated"), "error"); return; }
        try { before = await getJSON(base + "cart.js"); if (!Array.isArray(before.items)) throw new Error("Invalid cart"); }
        catch (_) { this.message(this.t("cartReadFailed"), "error"); return; }
        if (this.isConnected === false || this.lifecycleVersion !== startingLifecycle || String(this.requestInput().bedVariantId) !== String(startingVariant)) return;
        var drawer = document.querySelector("cart-drawer");
        var sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map(function (section) { return section.id; }) : ["cart-icon-bubble"];
        if (drawer && drawer.setActiveElement) drawer.setActiveElement(this.action);
        var payload = { items: plan.items, sections: sections, sections_url: location.pathname + location.search };
        this.saveAttempt({ pending: true, at: Date.now(), selection: Core.cloneSelection(this.selection), items: plan.items, reviewed: false });
        postStarted = true;
        this.message(this.t("adding"));
        response = await getJSON(base + "cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", "Accept": "application/json" }, body: JSON.stringify(payload) }, 30000);
        var returned = Array.isArray(response.items) ? response.items : response.id ? [response] : [];
        if (!plan.items.every(function (item) { return returned.some(function (line) { return String(line.variant_id || line.id) === String(item.id); }); })) throw new Error("Incomplete cart addition response");
        var after = null;
        try { after = await getJSON(base + "cart.js"); } catch (_) {}
        if (after && Core.reconcile(before, after, plan.items).status !== "confirmed") {
          this.blocked = true;
          this.message(this.t("uncertain"), "error");
          this.renderCart(response, after, false);
          return;
        }
        this.complete(response, after, plan);
      } catch (error) {
        if (!postStarted) { this.message(this.t("preflightFailed"), "error"); return; }
        var current = null;
        try { current = await getJSON(base + "cart.js"); } catch (_) {}
        var status = Core.reconcile(before, current, plan && plan.items || []).status;
        if (status === "confirmed") { this.complete(response, current, plan); return; }
        var definiteRejection = error.httpStatus >= 400 && error.httpStatus < 500 && error.httpStatus !== 429 && status === "unchanged";
        if (definiteRejection) {
          this.clearAttempt();
          this.message(this.t("rejected", { message: error.message }), "error");
          this.dispatchCart(current);
        } else {
          this.blocked = true;
          this.message(this.t(status === "partial" ? "partial" : "uncertain"), "error");
          this.dispatchCart(current);
        }
      } finally {
        this.setBusy(false);
      }
    }

    dispatchCart(cart, replacingDrawer) {
      if (cart && Array.isArray(cart.items)) document.dispatchEvent(new CustomEvent("cart:change", { detail: { cart: cart, sectionId: "faf-bedroom-builder" } }));
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") publish(PUB_SUB_EVENTS.cartUpdate, { source: replacingDrawer ? "faf-bedroom-builder" : "cart-items", origin: "faf-bedroom-builder" });
    }

    renderCart(response, cart, open) {
      var drawer = document.querySelector("cart-drawer");
      if (!drawer || !response || !response.sections) { this.dispatchCart(cart); return false; }
      var valid = drawer.getSectionsToRender().every(function (section) {
        var text = response.sections[section.id];
        if (typeof text !== "string") return false;
        var parsed = new DOMParser().parseFromString(text, "text/html");
        return !!parsed.querySelector(section.selector || ".shopify-section");
      });
      if (!valid || open === false) { this.dispatchCart(cart); return false; }
      try {
        this.dispatchCart(cart, true);
        drawer.classList.remove("is-empty");
        drawer.renderContents(response);
        return true;
      } catch (_) { this.dispatchCart(cart); return false; }
    }

    complete(response, cart, plan) {
      this.clearAttempt();
      this.blocked = false;
      this.successShown = true;
      plan.items.forEach((item) => document.dispatchEvent(new CustomEvent("variant:add", { detail: { variant: { id: item.id }, quantity: item.quantity, formElement: this.getForm(), sectionId: this.dataset.section } })));
      var rendered = this.renderCart(response, cart, true);
      if (rendered) this.message(this.t("added"));
      else {
        this.message(this.t("drawerFailed"), "error");
        this.retry.hidden = true;
        this.review.hidden = false;
        this.reviewReset.hidden = true;
      }
      this.updateSticky();
    }
  }

  customElements.define("faf-bedroom-builder", BedroomBuilder);
})();
